/**
 * Chat Store — conversation threads kept on the user's machine.
 *
 * Threads are held in one origin-wide store rather than partitioned per
 * workbook: the only workbook identifier Office offers is the document URL,
 * which is empty for unsaved files and changes on rename or move. Using it as
 * a key would silently orphan a user's history, so the workbook name is a
 * *tag* the UI filters on, never a partition.
 *
 * Storage is injected so the store can be exercised without a browser.
 */

import {ActionOutcome} from "./actionExecutor";
import {TurnMessage, pruneAllSnapshots} from "./conversationHistory";
import {recordTelemetryEvent} from "./telemetry";
import type {StorageTelemetryEvent} from "./telemetry";

export const STORAGE_KEY_THREADS = "librechat_chat_threads";

/**
 * Threads retained. `localStorage` is capped at roughly 5MB for the whole
 * origin, shared with the settings keys, so the store is deliberately small.
 */
export const MAX_THREADS = 10;

const MAX_TITLE_LENGTH = 40;

/** The subset of the Web Storage API the store needs. */
export interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

export type StorageTelemetry = (event: StorageTelemetryEvent) => void;

/** A chat bubble as rendered in the task pane. */
export interface StoredMessage {
  role: "user" | "assistant" | "system";
  content: string;
  /** Present on action-result bubbles only; drives the bubble's styling. */
  outcome?: ActionOutcome;
  actionBlocks?: string[];
}

export interface ChatThread {
  id: string;
  title: string;
  /** Workbook the thread was started in; the thread list filters on it. */
  workbookName: string;
  createdAt: number;
  updatedAt: number;
  /** What the user sees. */
  messages: StoredMessage[];
  /** What the model remembers. */
  history: TurnMessage[];
}

function defaultStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function byRecentActivity(a: ChatThread, b: ChatThread): number {
  return b.updatedAt - a.updatedAt;
}

export function loadThreads(
  storage = defaultStorage(),
  report: StorageTelemetry = recordTelemetryEvent,
): ChatThread[] {
  if (!storage) return [];
  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY_THREADS);
  } catch {
    report({type: "thread_store_corrupt", reason: "read_failure"});
    return [];
  }
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      report({type: "thread_store_corrupt", reason: "invalid_shape"});
      return [];
    }
    const threads = parsed.filter(isThread);
    if (threads.length !== parsed.length) {
      report({type: "thread_store_corrupt", reason: "invalid_shape"});
    }
    return threads.sort(byRecentActivity);
  } catch {
    report({type: "thread_store_corrupt", reason: "invalid_json"});
    return [];
  }
}

function isThread(value: unknown): value is ChatThread {
  const thread = value as ChatThread | null;
  return (
    !!thread &&
    typeof thread.id === "string" &&
    Array.isArray(thread.messages) &&
    Array.isArray(thread.history)
  );
}

function isQuotaExceeded(error: unknown): boolean {
  const name = (error as {name?: string} | null)?.name;
  return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED";
}

/**
 * Writes the list, shedding the oldest thread and retrying once if the browser
 * reports the quota is full — losing the least recent conversation beats
 * throwing in the middle of a chat. Any other failure leaves storage alone.
 * The list actually on disk is returned, so callers never render threads that
 * were not saved.
 */
function write(
  threads: ChatThread[],
  storage: StorageLike,
  report: StorageTelemetry,
): ChatThread[] {
  const capped = [...threads].sort(byRecentActivity).slice(0, MAX_THREADS);
  if (threads.length > capped.length) {
    report({type: "thread_evicted", reason: "max_threads"});
  }
  try {
    storage.setItem(STORAGE_KEY_THREADS, JSON.stringify(capped));
    return capped;
  } catch (error) {
    if (!isQuotaExceeded(error)) {
      report({type: "thread_save_failure", reason: "storage_error"});
      return loadThreads(storage, report);
    }
    report({type: "thread_save_failure", reason: "quota"});
    const trimmed = capped.slice(0, -1);
    try {
      storage.setItem(STORAGE_KEY_THREADS, JSON.stringify(trimmed));
      report({type: "thread_evicted", reason: "quota"});
      return trimmed;
    } catch {
      report({type: "thread_save_failure", reason: "quota_retry_failed"});
      return loadThreads(storage, report);
    }
  }
}

/**
 * A thread is saved as soon as the question is asked, which can catch the
 * bubble the reply is still streaming into. Persisting it would restore a
 * conversation ending in a blank message.
 */
function withoutPendingBubble(messages: StoredMessage[]): StoredMessage[] {
  return messages.filter(
    (message) =>
      message.role !== "assistant" ||
      message.content !== "" ||
      (message.actionBlocks?.length ?? 0) > 0,
  );
}

export function saveThread(
  thread: ChatThread,
  storage = defaultStorage(),
  report: StorageTelemetry = recordTelemetryEvent,
): ChatThread[] {
  if (!storage) return [];
  const stored: ChatThread = {
    ...thread,
    messages: withoutPendingBubble(thread.messages),
    history: pruneAllSnapshots(thread.history),
  };
  const others = loadThreads(storage, report).filter((t) => t.id !== thread.id);
  return write([stored, ...others], storage, report);
}

export function deleteThread(
  id: string,
  storage = defaultStorage(),
  report: StorageTelemetry = recordTelemetryEvent,
): ChatThread[] {
  if (!storage) return [];
  return write(
    loadThreads(storage, report).filter((t) => t.id !== id),
    storage,
    report,
  );
}

export function renameThread(
  id: string,
  title: string,
  storage = defaultStorage(),
  report: StorageTelemetry = recordTelemetryEvent,
): ChatThread[] {
  if (!storage) return [];
  return write(
    loadThreads(storage, report).map((t) => (t.id === id ? {...t, title} : t)),
    storage,
    report,
  );
}

/** Titles come from the user's opening message — no extra model round-trip. */
export function deriveThreadTitle(firstUserMessage: string): string {
  const oneLine = firstUserMessage.replace(/\s+/g, " ").trim();
  if (!oneLine) return "New chat";
  if (oneLine.length <= MAX_TITLE_LENGTH) return oneLine;
  return `${oneLine.slice(0, MAX_TITLE_LENGTH).trimEnd()}…`;
}

let threadIdCounter = 0;

/**
 * Not a security context, but CodeQL flags `Math.random()` wherever it
 * appears regardless — `crypto.getRandomValues` is used instead, falling
 * back to a counter (not `Math.random()`) only when no Web Crypto API
 * exists at all.
 */
export function createThreadId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(8));
    const suffix = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
      "",
    );
    return `${Date.now().toString(36)}-${suffix}`;
  }
  threadIdCounter += 1;
  return `${Date.now().toString(36)}-${threadIdCounter.toString(36)}`;
}
